import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { FaroErrorBoundary } from '@cortex/faro/react';
import { config } from './config.js';
import { RootLayout } from './app-layout.js';
import { LoginPage } from './routes/LoginPage.js';
import { DashboardHomePage } from './routes/DashboardHomePage.js';
import { WeeklyReportListPage } from './routes/WeeklyReportListPage.js';
import { WeeklyReportDetailPage } from './routes/WeeklyReportDetailPage.js';
import { WeeklyReportEditPage } from './routes/WeeklyReportEditPage.js';
import { KpiPage } from './routes/KpiPage.js';
import { RevenuePage } from './routes/RevenuePage.js';
import { CodeMapPage } from './routes/CodeMapPage.js';
import { ReleaseHistoryPage } from './routes/ReleaseHistoryPage.js';
import { DataCatalogPage } from './routes/DataCatalogPage.js';
import { NotificationPage } from './routes/NotificationPage.js';
import { WithdrawalAnalysisPage } from './routes/WithdrawalAnalysisPage.js';
import { CsInquiryDashboardPage } from './routes/CsInquiryDashboardPage.js';
import { FaqMonitorPage } from './routes/FaqMonitorPage.js';
import { CsInquiryChatsPage } from './routes/CsInquiryChatsPage.js';
import { CsInquiryAiAnalysisPage } from './routes/CsInquiryAiAnalysisPage.js';
import { StylingAnalysisPage } from './routes/StylingAnalysisPage.js';
import { StylistPerformancePage } from './routes/StylistPerformancePage.js';
import { FunnelAnalysisPage } from './routes/FunnelAnalysisPage.js';
import { CouponUsagePage } from './routes/CouponUsagePage.js';
import { DataIntegrityPage } from './routes/DataIntegrityPage.js';
import { CostAccountingPage } from './routes/CostAccountingPage.js';
import { MemberListPage } from './routes/MemberListPage.js';
import { MemberDetailPage } from './routes/MemberDetailPage.js';
import { TranscriptionListPage } from './routes/TranscriptionListPage.js';
import { TranscriptionCreatePage } from './routes/TranscriptionCreatePage.js';
import { TranscriptionDetailPage } from './routes/TranscriptionDetailPage.js';
import { BudgetActualPage } from './routes/BudgetActualPage.js';
import { MemberDemographicsPage } from './routes/MemberDemographicsPage.js';
import { PermissionsPage } from './routes/PermissionsPage.js';
import PurchaseAnalysisPage from './routes/PurchaseAnalysisPage.js';
import { KeyIssuesPage } from './routes/KeyIssuesPage.js';
import { ProductBacklogKeyPage, ProductBacklogPage } from './routes/ProductBacklogPage.js';
import { IssueMgmtLayout } from './components/issue-mgmt/IssueMgmtLayout.js';
import { IssueMgmtDetailPage } from './routes/IssueMgmtDetailPage.js';
import { IssueMgmtListPage } from './routes/IssueMgmtListPage.js';
import { IssueMgmtSettingsPage } from './routes/IssueMgmtSettingsPage.js';
import { IssueMgmtTodoPage, parseTodoSearch } from './routes/IssueMgmtTodoPage.js';
import { parseIssueListSearch, toIssueListSearchParams } from './lib/issue-mgmt-search.js';
import { ForbiddenPage } from './routes/ForbiddenPage.js';
import { KnowledgeMetaDetailPage } from './routes/KnowledgeMetaDetailPage.js';
import { KnowledgeMetaPage } from './routes/KnowledgeMetaPage.js';
import { ReportListPage } from './routes/ReportListPage.js';
import { ItemCatalogPage } from './routes/ItemCatalogPage.js';
import { CoordeCapacityPage } from './routes/CoordeCapacityPage.js';
import { CoordeHistoryPage } from './routes/CoordeHistoryPage.js';
import { StylingReviewDetailPage } from './routes/StylingReviewDetailPage.js';
import { StylingReviewListPage } from './routes/StylingReviewListPage.js';
import { CoordeRequestPage } from './routes/CoordeRequestPage.js';
import { CoordeRunPage } from './routes/CoordeRunPage.js';
import { ReportDetailPage } from './routes/ReportDetailPage.js';

/** @graph-connects none */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: config.query.staleTime,
      gcTime: config.query.gcTime,
      retry: config.query.retry,
      refetchOnWindowFocus: config.query.refetchOnWindowFocus,
    },
  },
});

/** @graph-connects none */
const rootRoute = createRootRoute({
  component: RootLayout,
});
/** @graph-connects none */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
/** @graph-connects none */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardHomePage,
});
/** @graph-connects none */
const weeklyReportListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/weekly-report',
  component: WeeklyReportListPage,
});
/** @graph-connects none */
const weeklyReportNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/weekly-report/new',
  component: WeeklyReportEditPage,
});
/** @graph-connects none */
const weeklyReportDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/weekly-report/$id',
  component: WeeklyReportDetailPage,
});
/** @graph-connects none */
const weeklyReportEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/weekly-report/$id/edit',
  component: WeeklyReportEditPage,
});
/** @graph-connects none */
const kpiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kpi',
  component: KpiPage,
});
/** @graph-connects none */
const revenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/revenue',
  component: RevenuePage,
});
/** @graph-connects none */
const codeMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/code-map',
  component: CodeMapPage,
});
/** @graph-connects none */
const releaseHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/release-history',
  component: ReleaseHistoryPage,
});
/** @graph-connects none */
const dataCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-catalog',
  component: DataCatalogPage,
});
/** @graph-connects none */
const withdrawalAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/withdrawal-analysis',
  component: WithdrawalAnalysisPage,
});
/** @graph-connects none */
const csInquiryDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cs/inquiry',
  component: CsInquiryDashboardPage,
});
/** @graph-connects none */
const csInquiryChatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cs/inquiry/chats',
  component: CsInquiryChatsPage,
});
/** @graph-connects none */
const csInquiryAiAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cs/inquiry/ai-analysis',
  component: CsInquiryAiAnalysisPage,
});
/** @graph-connects none */
const faqMonitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq-monitor',
  component: FaqMonitorPage,
});
/** @graph-connects none */
const stylingAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/styling-analysis',
  component: StylingAnalysisPage,
});
/** @graph-connects none */
const stylistPerformanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stylist-performance',
  component: StylistPerformancePage,
});
/** @graph-connects none */
const funnelAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/funnel-analysis',
  component: FunnelAnalysisPage,
});
/** @graph-connects none */
const notificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationPage,
});
/** @graph-connects none */
const dataIntegrityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-integrity',
  component: DataIntegrityPage,
});
/** @graph-connects none */
const costAccountingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cost-accounting',
  component: CostAccountingPage,
});
/** @graph-connects none */
const memberListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members',
  component: MemberListPage,
});
/** @graph-connects none */
const memberDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/$arthurUserId',
  component: MemberDetailPage,
});
/** @graph-connects none */
const transcriptionListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcriptions',
  component: TranscriptionListPage,
});
/** @graph-connects none */
const transcriptionCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcriptions/new',
  component: TranscriptionCreatePage,
});
/** @graph-connects none */
const transcriptionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcriptions/$id',
  component: TranscriptionDetailPage,
});
/** @graph-connects none */
const memberDemographicsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/member-demographics',
  component: MemberDemographicsPage,
});
/** @graph-connects none */
const budgetActualRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/budget-actual',
  component: BudgetActualPage,
});
/** @graph-connects none */
const couponUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coupon-usage',
  component: CouponUsagePage,
});
/** @graph-connects none */
const purchaseAnalysisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/purchase-analysis',
  component: PurchaseAnalysisPage,
});
/** @graph-connects none */
const keyIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/key-issues',
  component: KeyIssuesPage,
});
/** @graph-connects none */
const productBacklogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backlog',
  component: ProductBacklogPage,
});
/** キーで案件を開く URL（課題管理などからの入口）。 @graph-connects none */
const productBacklogKeyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backlog/$key',
  component: ProductBacklogKeyPage,
});
/** @graph-connects none */
const issueMgmtRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/issue-mgmt',
  component: IssueMgmtLayout,
});

/** 課題管理を開いたら、最初は課題の一覧を出す。 @graph-connects none */
const issueMgmtIndexRoute = createRoute({
  getParentRoute: () => issueMgmtRoute,
  path: '/',
  beforeLoad: () => {
    redirect({ to: '/issue-mgmt/issues', throw: true });
  },
});

/** @graph-connects none */
const issueMgmtListRoute = createRoute({
  getParentRoute: () => issueMgmtRoute,
  path: '/issues',
  component: IssueMgmtListPage,
  validateSearch: (search: Record<string, unknown>) =>
    toIssueListSearchParams(parseIssueListSearch(search)),
});

/** @graph-connects none */
const issueMgmtDetailRoute = createRoute({
  getParentRoute: () => issueMgmtRoute,
  path: '/issues/$issueId',
  component: IssueMgmtDetailPage,
});

/** @graph-connects none */
const issueMgmtTodoRoute = createRoute({
  getParentRoute: () => issueMgmtRoute,
  path: '/todo',
  component: IssueMgmtTodoPage,
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = parseTodoSearch(search);
    return {
      ...(parsed.card === null ? {} : { card: parsed.card }),
      ...(parsed.panel === null ? {} : { panel: parsed.panel }),
    };
  },
});

/** @graph-connects none */
const issueMgmtSettingsRoute = createRoute({
  getParentRoute: () => issueMgmtRoute,
  path: '/settings',
  component: IssueMgmtSettingsPage,
});

/** @graph-connects none */
const permissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/permissions',
  component: PermissionsPage,
});
/** @graph-connects none */
const knowledgeMetaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/meta-docs',
  component: KnowledgeMetaPage,
});
/** @graph-connects none */
const knowledgeMetaDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/meta-docs/$docId',
  component: KnowledgeMetaDetailPage,
});
/** @graph-connects none */
const reportListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportListPage,
});
/** @graph-connects none */
const reportDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/$id',
  component: ReportDetailPage,
});
/** @graph-connects none */
const stylingReviewListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/styling-review',
  component: StylingReviewListPage,
});
/** @graph-connects none */
const stylingReviewDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/styling-review/$phoenixOrderId',
  component: StylingReviewDetailPage,
});
/** @graph-connects none */
const itemCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/item-catalog',
  component: ItemCatalogPage,
});
/** @graph-connects none */
const coordeCapacityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coorde-capacity',
  component: CoordeCapacityPage,
});
/** @graph-connects none */
const coordeHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coorde',
  component: CoordeHistoryPage,
});
/** @graph-connects none */
const coordeRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coorde/new',
  component: CoordeRequestPage,
});
/** @graph-connects none */
const coordeRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coorde/$runId',
  component: CoordeRunPage,
});
/** @graph-connects none */
const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forbidden',
  component: ForbiddenPage,
});
/** @graph-connects none */
const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  weeklyReportListRoute,
  weeklyReportNewRoute,
  weeklyReportDetailRoute,
  weeklyReportEditRoute,
  kpiRoute,
  revenueRoute,
  codeMapRoute,
  releaseHistoryRoute,
  dataCatalogRoute,
  withdrawalAnalysisRoute,
  csInquiryDashboardRoute,
  csInquiryChatsRoute,
  csInquiryAiAnalysisRoute,
  faqMonitorRoute,
  stylingAnalysisRoute,
  stylistPerformanceRoute,
  funnelAnalysisRoute,
  notificationRoute,
  dataIntegrityRoute,
  costAccountingRoute,
  memberListRoute,
  memberDetailRoute,
  transcriptionListRoute,
  transcriptionCreateRoute,
  transcriptionDetailRoute,
  memberDemographicsRoute,
  budgetActualRoute,
  couponUsageRoute,
  purchaseAnalysisRoute,
  keyIssuesRoute,
  productBacklogRoute,
  productBacklogKeyRoute,
  issueMgmtRoute.addChildren([
    issueMgmtIndexRoute,
    issueMgmtListRoute,
    issueMgmtDetailRoute,
    issueMgmtTodoRoute,
    issueMgmtSettingsRoute,
  ]),
  permissionsRoute,
  reportListRoute,
  knowledgeMetaRoute,
  knowledgeMetaDetailRoute,
  reportDetailRoute,
  stylingReviewListRoute,
  stylingReviewDetailRoute,
  itemCatalogRoute,
  coordeCapacityRoute,
  coordeHistoryRoute,
  coordeRequestRoute,
  coordeRunRoute,
  forbiddenRoute,
]);
/** @graph-connects none */
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub root app with Query provider, router, and Faro error boundary.
 * @graph-connects none
 */
export function App() {
  return (
    <FaroErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </FaroErrorBoundary>
  );
}
